/**
 * BulkPaymentUploadModal
 * 3-step wizard inside a Dialog:
 *   1. Upload: select CSV + choose payment type
 *   2. Preview: review ready / flagged / unmatched rows
 *   3. Results: import summary
 */

import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload, Eye, CheckCircle2, AlertTriangle, XCircle,
  Check, X, ChevronLeft, Info,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useUploadPaymentsPreview, useUploadPaymentsCommit } from '../../hooks/usePayments';

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------
function StepIndicator({ step }) {
  const { t } = useTranslation();
  const STEPS = [
    { id: 1, icon: Upload,       labelKey: 'payments.upload.step1Title' },
    { id: 2, icon: Eye,          labelKey: 'payments.upload.step2Title' },
    { id: 3, icon: CheckCircle2, labelKey: 'payments.upload.step3Title' },
  ];
  return (
    <div className="flex items-center mt-4">
      {STEPS.map((s, idx) => {
        const Icon = s.icon;
        const isActive    = s.id === step;
        const isCompleted = s.id < step;
        return (
          <React.Fragment key={s.id}>
            <div className="flex flex-col items-center flex-1">
              <div className={[
                'w-9 h-9 rounded-full flex items-center justify-center transition-colors',
                isActive    ? 'bg-blue-600 text-white dark:bg-blue-500' : '',
                isCompleted ? 'bg-green-600 text-white dark:bg-green-500' : '',
                !isActive && !isCompleted ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500' : '',
              ].join(' ')}>
                <Icon className="w-4 h-4" />
              </div>
              <span className={`text-xs mt-1.5 text-center ${isActive ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {t(s.labelKey)}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 -mt-7 ${s.id < step ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — file upload + payment type
// ---------------------------------------------------------------------------
const PAYMENT_METHODS = ['STRIPE', 'BANK_TRANSFER', 'CARD', 'CASH', 'CHEQUE', 'OTHER'];

function UploadStep({ file, setFile, paymentMethod, setPaymentMethod, onPreview, isPreviewing }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (f) => {
    if (f && f.name.toLowerCase().endsWith('.csv')) setFile(f);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium mb-2">{t('payments.upload.selectFile')}</label>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={[
            'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
            dragging
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800/50',
          ].join(' ')}
        >
          <input ref={inputRef} type="file" accept=".csv" className="hidden"
            onChange={(e) => handleFile(e.target.files[0])} />
          <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          {file ? (
            <div className="space-y-1">
              <p className="font-medium text-green-700 dark:text-green-400">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
              <Button type="button" variant="outline" size="sm" className="mt-2"
                onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                <X className="w-3 h-3 mr-1" /> {t('common.remove')}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('payments.upload.dragDrop')}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('payments.upload.paymentMethod')}</label>
        <div className="grid grid-cols-3 gap-2">
          {PAYMENT_METHODS.map((pm) => (
            <button key={pm} type="button" onClick={() => setPaymentMethod(pm)}
              className={[
                'py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
                paymentMethod === pm
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400',
              ].join(' ')}>
              {t(`payments.method.${pm}`)}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{t('payments.upload.paymentMethodHint')}</p>
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={onPreview} disabled={!file || isPreviewing}>
          {isPreviewing ? t('payments.upload.previewing') : t('payments.upload.previewButton')}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flag badge + match badge
// ---------------------------------------------------------------------------
function FlagBadge({ flag }) {
  const { t } = useTranslation();
  const styles = {
    potential_duplicate: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    travel_flag:         'bg-amber-100  text-amber-700  dark:bg-amber-900/40  dark:text-amber-300',
    overpayment:         'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${styles[flag] || ''}`}>
      {t(`payments.upload.flag.${flag}`)}
    </span>
  );
}

function MatchBadge({ method }) {
  const { t } = useTranslation();
  const styles = {
    student_id: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    email:      'bg-blue-100  text-blue-700  dark:bg-blue-900/40  dark:text-blue-300',
    name:       'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${styles[method] || ''}`}>
      {t(`payments.upload.match.${method}`)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Flag action explanation (what happens when this row is imported)
// ---------------------------------------------------------------------------
function FlagAction({ row }) {
  const { t } = useTranslation();

  if (row.flag === 'travel_flag') {
    return (
      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>{t('payments.upload.flag.travel_flag_action', {
          amount: `€${parseFloat(row.amount).toFixed(2)}`,
          name: row.student_name,
        })}</span>
      </div>
    );
  }

  if (row.flag === 'overpayment') {
    const excess = parseFloat(row.overpayment_amount || 0).toFixed(2);
    const travelPrice = parseFloat(row.travel_price || 0);
    const excessNum = parseFloat(row.overpayment_amount || 0);
    const travelMismatch = travelPrice > 0 && Math.abs(excessNum - travelPrice) > 0.01;
    return (
      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded px-2 py-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          {t('payments.upload.flag.overpayment_action', { excess: `€${excess}` })}
          {travelMismatch && (
            <span className="block mt-0.5 text-orange-600 dark:text-orange-400">
              {t('payments.upload.flag.overpayment_travel_mismatch', {
                excess: `€${excess}`,
                travel: `€${travelPrice.toFixed(2)}`,
              })}
            </span>
          )}
        </span>
      </div>
    );
  }

  if (row.flag === 'potential_duplicate') {
    return (
      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 rounded px-2 py-1.5">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>{t('payments.upload.flag.potential_duplicate_action')}</span>
      </div>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Step 2 — preview (read-only, imports everything)
// ---------------------------------------------------------------------------
function PreviewStep({ preview, onBack, onImport, isImporting }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('ready');

  const { ready = [], flagged = [], unmatched = [] } = preview;
  const total = ready.length + flagged.length + unmatched.length;

  const formatAmount = (a) => `€${parseFloat(a).toFixed(2)}`;
  const formatDate   = (d) => d ? new Date(d).toLocaleDateString('en-IE') : '—';

  const tabs = [
    { key: 'ready',     label: t('payments.upload.readyTab',     { count: ready.length }),     icon: Check,         colour: 'text-green-600 dark:text-green-400' },
    { key: 'flagged',   label: t('payments.upload.flaggedTab',   { count: flagged.length }),   icon: AlertTriangle, colour: 'text-amber-600 dark:text-amber-400' },
    { key: 'unmatched', label: t('payments.upload.unmatchedTab', { count: unmatched.length }), icon: XCircle,       colour: 'text-orange-600 dark:text-orange-400' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm items-center">
        <span className="font-medium text-green-600 dark:text-green-400">
          {t('payments.upload.summaryReady', { count: ready.length })}
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="font-medium text-amber-600 dark:text-amber-400">
          {t('payments.upload.summaryFlagged', { count: flagged.length })}
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <span className="font-medium text-orange-600 dark:text-orange-400">
          {t('payments.upload.summaryUnmatched', { count: unmatched.length })}
        </span>
        {unmatched.length > 0 && (
          <span className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full">
            {t('payments.upload.unmatchedWillSave')}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b dark:border-gray-700">
        <div className="flex gap-1">
          {tabs.map(({ key, label, icon: Icon, colour }) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={[
                'flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                tab === key
                  ? `border-blue-500 ${colour}`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
              ].join(' ')}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="overflow-auto max-h-64">

        {/* Ready */}
        {tab === 'ready' && (
          ready.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">{t('payments.upload.noReady')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.student')}</th>
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.studentId')}</th>
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.matchMethod')}</th>
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.amount')}</th>
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.balanceDue')}</th>
                  <th className="pb-2 font-medium">{t('payments.upload.col.date')}</th>
                </tr>
              </thead>
              <tbody>
                {ready.map((row) => (
                  <tr key={row.row} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-2 pr-4 font-medium">{row.student_name}</td>
                    <td className="py-2 pr-4 text-gray-500">{row.student_id}</td>
                    <td className="py-2 pr-4"><MatchBadge method={row.match_method} /></td>
                    <td className="py-2 pr-4 tabular-nums">{formatAmount(row.amount)}</td>
                    <td className="py-2 pr-4 tabular-nums text-gray-500">{formatAmount(row.balance_due)}</td>
                    <td className="py-2 text-gray-500">{formatDate(row.payment_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Flagged — shown with flag info, no checkboxes, all will be imported */}
        {tab === 'flagged' && (
          flagged.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">{t('payments.upload.noFlagged')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.student')}</th>
                  <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.amount')}</th>
                  <th className="pb-2 font-medium">{t('payments.upload.col.flag')}</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map((row) => (
                  <tr key={row.row} className="border-b dark:border-gray-800 align-top">
                    <td className="py-3 pr-4">
                      <div className="font-medium">{row.student_name}</div>
                      <div className="text-xs text-gray-400">{row.student_id}</div>
                      <MatchBadge method={row.match_method} />
                    </td>
                    <td className="py-3 pr-4 tabular-nums font-medium">
                      {`€${parseFloat(row.amount).toFixed(2)}`}
                      <div className="text-xs text-gray-500 font-normal">
                        {t('payments.upload.col.balanceDue')}: {`€${parseFloat(row.balance_due).toFixed(2)}`}
                      </div>
                    </td>
                    <td className="py-3">
                      <FlagBadge flag={row.flag} />
                      <FlagAction row={row} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* Unmatched — will be saved without student link */}
        {tab === 'unmatched' && (
          unmatched.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">{t('payments.upload.noUnmatched')}</p>
          ) : (
            <>
              <div className="mb-3 flex items-start gap-2 text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-lg px-3 py-2.5">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{t('payments.upload.unmatchedSaveExplain')}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.student')}</th>
                    <th className="pb-2 pr-4 font-medium">{t('payments.upload.col.amount')}</th>
                    <th className="pb-2 font-medium">{t('payments.upload.col.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatched.map((row) => (
                    <tr key={row.row} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-2 pr-4">
                        <div className="font-medium">{row.first_name} {row.last_name}</div>
                        <div className="text-xs text-gray-400">{row.parent_email}</div>
                      </td>
                      <td className="py-2 pr-4 tabular-nums">€{parseFloat(row.amount || 0).toFixed(2)}</td>
                      <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )
        )}
      </div>

      <div className="flex justify-between pt-2 border-t dark:border-gray-800">
        <Button type="button" variant="outline" onClick={onBack}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('common.back')}
        </Button>
        <Button type="button" onClick={onImport} disabled={total === 0 || isImporting}>
          {isImporting
            ? t('payments.upload.importing')
            : t(`payments.upload.importAllButton${total !== 1 ? '_plural' : ''}`, { count: total })}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — results
// ---------------------------------------------------------------------------
function ResultsStep({ result, onDone }) {
  const { t } = useTranslation();
  const { created = 0, unmatched_created = 0, travel_updated = 0, skipped = 0 } = result;

  return (
    <div className="space-y-6 text-center py-4">
      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
      </div>
      <div>
        <h3 className="text-lg font-semibold">{t('payments.upload.successTitle')}</h3>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {t('payments.upload.successDescription', { created })}
        </p>
      </div>
      <div className="flex justify-center gap-6 text-sm flex-wrap">
        {unmatched_created > 0 && (
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-500 dark:text-orange-400">{unmatched_created}</div>
            <div className="text-gray-500">{t('payments.upload.unmatchedSaved', { count: unmatched_created })}</div>
          </div>
        )}
        {travel_updated > 0 && (
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{travel_updated}</div>
            <div className="text-gray-500">
              {t(`payments.upload.travelUpdated${travel_updated !== 1 ? '_plural' : ''}`, { count: travel_updated })}
            </div>
          </div>
        )}
        {skipped > 0 && (
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-400">{skipped}</div>
            <div className="text-gray-500">
              {t(`payments.upload.skipped${skipped !== 1 ? '_plural' : ''}`, { count: skipped })}
            </div>
          </div>
        )}
      </div>
      <Button type="button" onClick={onDone}>{t('payments.upload.doneButton')}</Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export default function BulkPaymentUploadModal({ isOpen, onClose, onComplete }) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [step, setStep]                   = useState(1);
  const [file, setFile]                   = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('STRIPE');
  const [preview, setPreview]             = useState(null);
  const [result, setResult]               = useState(null);

  const previewMutation = useUploadPaymentsPreview();
  const commitMutation  = useUploadPaymentsCommit();

  const resetState = () => {
    setStep(1);
    setFile(null);
    setPaymentMethod('STRIPE');
    setPreview(null);
    setResult(null);
  };

  const handleOpenChange = (open) => {
    if (!open) {
      if (step === 2 && !window.confirm(t('payments.upload.confirmClose'))) return;
      resetState();
      onClose();
    }
  };

  const handlePreview = () => {
    if (!file) {
      toast({ title: t('payments.upload.fileRequired'), variant: 'destructive' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('payment_method', paymentMethod);
    previewMutation.mutate(fd, {
      onSuccess: (data) => {
        setPreview(data);
        setStep(2);
      },
      onError: () => toast({ title: t('payments.upload.previewError'), variant: 'destructive' }),
    });
  };

  const handleImport = () => {
    const { ready = [], flagged = [], unmatched = [] } = preview;

    const matchedRows = [...ready, ...flagged].map(r => ({
      student_uuid: r.student_uuid,
      amount:       r.amount,
      payment_date: r.payment_date,
      registration: r.registration || '',
      flag:         r.flag || '',
    }));

    const unmatchedRows = unmatched.map(r => ({
      flag:         'unmatched',
      amount:       r.amount,
      payment_date: r.payment_date || new Date().toISOString(),
      registration: r.registration || '',
      first_name:   r.first_name || '',
      last_name:    r.last_name || '',
      parent_email: r.parent_email || '',
    }));

    commitMutation.mutate(
      { payment_method: paymentMethod, rows: [...matchedRows, ...unmatchedRows] },
      {
        onSuccess: (data) => { setResult(data); setStep(3); },
        onError: () => toast({ title: t('payments.upload.errorTitle'), variant: 'destructive' }),
      }
    );
  };

  const handleDone = () => {
    onComplete?.();
    resetState();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('payments.upload.title')}</DialogTitle>
          <StepIndicator step={step} />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pt-4">
          {step === 1 && (
            <UploadStep
              file={file} setFile={setFile}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              onPreview={handlePreview} isPreviewing={previewMutation.isLoading}
            />
          )}
          {step === 2 && preview && (
            <PreviewStep
              preview={preview}
              onBack={() => setStep(1)}
              onImport={handleImport}
              isImporting={commitMutation.isLoading}
            />
          )}
          {step === 3 && result && (
            <ResultsStep result={result} onDone={handleDone} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
